import React from "react";
import { Table, Card, Descriptions, Tag, Popover, Button, Space, Tooltip, Pagination, Typography, Spin } from "antd";
import moment from "moment";
import { Link } from "react-router-dom";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import useIsMobile from "../../hooks/useIsMobile";

const { Title } = Typography;

const renderRow = (key, value) => (
    <div
        style={{
            display: "flex",
            borderBottom: "1px solid #e8e8e8",
            alignItems: "flex-start",
            minHeight: "44px",
            justifyContent: "flex-start",
        }}>
        <div
            style={{
                width: "35%",
                padding: "8px",
                fontWeight: "bold",
                borderRight: "1px solid #e8e8e8",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                textAlign: "left",
            }}>
            <strong>{key}: </strong>
        </div>
        <div
            style={{
                width: "65%",
                padding: "8px",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                textAlign: "left",
                wordBreak: "break-word"
            }}>
            <span>{value}</span>
        </div>
    </div>
);

const MobileWordCountRequestsTable = ({
    data,
    loading,
    pagination,
    setPagination,
    activeTab,
    showConfirm,
}) => {
    return (
        <Spin spinning={loading}>
            <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "10px", background: '#f2f6f9' }}>
                {data.map((record) => (
                    <div
                        key={record.id}
                        style={{
                            background: "white",
                            borderRadius: "8px",
                            overflow: "hidden",
                            marginTop: '10px'
                            // border: "1px solid #e8e8e8",
                            // boxShadow: "0 1px 2px #f0f1f2",
                        }}>
                        {renderRow("Order Key", (
                            <Link
                                to={`/order-management/my-orders/${record.task_key}`}
                                style={{ fontWeight: "bold" }}
                            >
                                {record.task_key}
                            </Link>
                        ))}
                        {renderRow("Requested By", record.requested_by_name || "N/A")}
                        {renderRow("Requested Words", record.requested_word_count || "0")}
                        {renderRow("Remarks", (
                            <Popover content={<div style={{ maxWidth: "300px" }}>{record.justification}</div>} title="Remarks">
                                <Button type="link" style={{ padding: 0 }}>View</Button>
                            </Popover>
                        ))}
                        {renderRow("Status", (() => {
                            let color = "blue";
                            const upperStatus = record.status?.toUpperCase();
                            if (upperStatus === "APPROVED") color = "green";
                            if (upperStatus === "REJECTED") color = "red";
                            if (upperStatus === "PENDING") color = "orange";
                            return <Tag color={color}>{record.status || "PENDING"}</Tag>;
                        })())}
                        {record.status?.toUpperCase() === "REJECTED" && renderRow("Rejection Reason", (
                            record.rejection_reason ? (
                                <Popover content={<div style={{ maxWidth: "300px" }}>{record.rejection_reason}</div>} title="Rejection Reason">
                                    <Button type="link" style={{ padding: 0 }}>View</Button>
                                </Popover>
                            ) : "N/A"
                        ))}
                        {renderRow("Action By", record.action_by_name || "N/A")}
                        {renderRow("Raised On", record.created_at ? moment(record.created_at).format("DD-MM-YYYY hh:mm A") : "N/A")}

                        {activeTab === "all" && record.status?.toUpperCase() === "PENDING" && (
                            <div style={{ padding: "8px", display: "flex", justifyContent: "space-around", borderTop: "1px solid #e8e8e8" }}>
                                <Button
                                    type="link"
                                    icon={<CheckOutlined />}
                                    style={{ color: "#52c41a" }}
                                    onClick={() => showConfirm(record.id, "APPROVED")}
                                >
                                    Approve
                                </Button>
                                <Button
                                    type="link"
                                    icon={<CloseOutlined />}
                                    danger
                                    onClick={() => showConfirm(record.id, "REJECTED")}
                                >
                                    Reject
                                </Button>
                            </div>
                        )}
                    </div>
                ))}
                <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={(page, pageSize) =>
                        setPagination((prev) => ({ ...prev, current: page, pageSize }))
                    }
                    showSizeChanger
                    style={{ textAlign: "center", marginTop: 16 }}
                />
            </div>
        </Spin>
    );
};

const WordCountRequestsTable = ({
    data,
    loading,
    pagination,
    setPagination,
    activeTab,
    showConfirm,
}) => {
    const isMobile = useIsMobile();

    const columns = [
        {
            title: "Order Key",
            dataIndex: "task_key",
            key: "task_key",
            render: (text) => (
                <Link
                    to={`/order-management/my-orders/${text}`}
                    style={{ fontWeight: "bold" }}
                >
                    {text}
                </Link>
            ),
        },
        {
            title: "Requested By",
            dataIndex: "requested_by_name",
            key: "requested_by_name",
        },
        {
            title: "Requested Words",
            dataIndex: "requested_word_count",
            key: "requested_word_count",
            sorter: (a, b) => a.requested_word_count - b.requested_word_count,
        },
        {
            title: "Remarks",
            dataIndex: "justification",
            key: "justification",
            render: (text) => (
                <Popover content={<div style={{ maxWidth: "300px" }}>{text}</div>} title="Remarks">
                    <Button type="link" style={{ padding: 0 }}>View</Button>
                </Popover>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => {
                let color = "blue";
                const upperStatus = status?.toUpperCase();
                if (upperStatus === "APPROVED") color = "green";
                if (upperStatus === "REJECTED") color = "red";
                if (upperStatus === "PENDING") color = "orange";
                return <Tag color={color}>{status || "PENDING"}</Tag>;
            },
        },
        {
            title: "Rejection Reason",
            dataIndex: "rejection_reason",
            key: "rejection_reason",
            render: (text, record) => (
                record.status?.toUpperCase() === "REJECTED" ? (
                    text ? (
                        <Popover content={<div style={{ maxWidth: "300px" }}>{text}</div>} title="Rejection Reason">
                            <Button type="link" style={{ padding: 0 }}>View</Button>
                        </Popover>
                    ) : "N/A"
                ) : "-"
            ),
        },
        {
            title: "Action By",
            dataIndex: "action_by_name",
            key: "action_by_name",
            render: (text) => text || "N/A",
        },
        {
            title: "Raised On",
            dataIndex: "created_at",
            key: "created_at",
            render: (date) => (date ? moment(date).format("DD-MM-YYYY hh:mm A") : "N/A"),
            sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix(),
        },
    ];

    if (activeTab === "all") {
        columns.push({
            title: "Action",
            key: "action",
            render: (_, record) => (
                <Space size="middle">
                    {record.status?.toUpperCase() === "PENDING" ? (
                        <>
                            <Tooltip title="Approve">
                                <Button
                                    type="primary"
                                    shape="circle"
                                    icon={<CheckOutlined />}
                                    style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                                    onClick={() => showConfirm(record.id, "APPROVED")}
                                />
                            </Tooltip>
                            <Tooltip title="Reject">
                                <Button
                                    type="primary"
                                    shape="circle"
                                    icon={<CloseOutlined />}
                                    danger
                                    onClick={() => showConfirm(record.id, "REJECTED")}
                                />
                            </Tooltip>
                        </>
                    ) : (
                        <span style={{ color: "#888", fontStyle: "italic" }}>No actions</span>
                    )}
                </Space>
            ),
        });
    }

    return (
        <div style={{ width: "100%" }}>
            {isMobile ? (
                <MobileWordCountRequestsTable
                    data={data}
                    loading={loading}
                    pagination={pagination}
                    setPagination={setPagination}
                    activeTab={activeTab}
                    showConfirm={showConfirm}
                />
            ) : (
                <Table
                    style={{ width: "100%" }}
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: (page, pageSize) => {
                            setPagination({ ...pagination, current: page, pageSize });
                        },
                        onShowSizeChange: (current, size) => {
                            setPagination({ ...pagination, current: 1, pageSize: size });
                        },
                        showSizeChanger: true,
                    }}
                />
            )}
        </div>
    );
};

export default WordCountRequestsTable;
